import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Trophy } from "@similarweb/icons";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Table, TextCell } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { percentageFilter } from "app/filters/ngFilters";

import { useMetricWidgetContext } from "../../../common/widget/donut-metric/MetricWidgetContext";
import type { IChartDataItem } from "../../../common/widget/donut-metric/types";
import { DomainCell } from "../../../common/widget/table/DomainCell";
import { rowIndexColumn } from "../../../common/widget/table/rowIndexColumn";

interface IShareMetricTableRow {
    domain: string;
    icon?: string | null;
    share: number;
    isPrimary: boolean;
    isMaxValue: boolean;
}

const toTableData = (chartData: IChartDataItem[]): IShareMetricTableRow[] =>
    chartData.map((item) => ({
        domain: item.name,
        icon: item.icon,
        share: item.y,
        isPrimary: !!item.isPrimary,
        isMaxValue: !!item.isMaxValue,
    }));

const useColumnsConfig = () => {
    const { translate } = useTranslate();
    return useMemo(
        () =>
            [
                rowIndexColumn,
                {
                    title: translate("activation.alerts.metrics.legend.domain"),
                    dataIndex: "domain",
                    render: (value: string, record: IShareMetricTableRow) => (
                        <DomainCell
                            domain={value}
                            icon={record.icon ?? undefined}
                            isPrimary={record.isPrimary}
                        >
                            {record.isMaxValue ? (
                                <Trophy
                                    size="xs"
                                    color={colorsPalettes.yellow[400]}
                                />
                            ) : null}
                        </DomainCell>
                    ),
                    minWidth: 200,
                },
                {
                    title: translate("activation.alerts.metrics.legend.share"),
                    dataIndex: "share",
                    align: "right",
                    render: (value: number, record: IShareMetricTableRow) => (
                        <TextCell fontVariant={record.isMaxValue ? "subtitle2" : "body2"}>
                            {`${percentageFilter()(value, 0)}%`}
                        </TextCell>
                    ),
                    width: 120,
                },
            ] satisfies ColumnsType<IShareMetricTableRow>,
        [translate],
    );
};

export const ShareMetricTable = () => {
    const {
        state: { fullData },
    } = useMetricWidgetContext();
    const tableData = useMemo(() => (fullData ? toTableData(fullData) : []), [fullData]);
    const columnsConfig = useColumnsConfig();

    return (
        <Table<IShareMetricTableRow>
            data={tableData}
            columnConfig={columnsConfig}
            tableLayout="auto"
            highlightedRowSelector={(record) => record.isPrimary}
        />
    );
};
